import { getWallets } from '@wallet-standard/app';

/**
 * Mobile wallet bounce.
 *
 * On mobile without an injected wallet provider, the wallet-adapter
 * library's deep-link path is flaky (universal-link timing, lost
 * user-gesture, etc.). When the user is about to do a wallet action
 * (login, buy, sell, burn) without a connected wallet, we open a
 * picker that lets them choose which wallet to bounce into. Each
 * wallet's universal-link opens our page inside that wallet's
 * in-app browser, where the standard wallet-adapter flow then runs
 * synchronously against the wallet's injected provider.
 *
 * Returns true if the picker was opened (or the legacy auto-redirect
 * fired). Caller should bail out of its current handler. Returns
 * false on desktop / when an injected wallet is already present —
 * caller should fall through to the normal wallet-adapter modal.
 */

// Module-level singleton — the <MobileWalletPicker> mounted in App.tsx
// registers itself here so any caller can trigger the picker without
// passing a ref or context handle through every wallet-action site.
let openPicker: ((anchor?: string) => void) | null = null;

export function registerMobileWalletPicker(fn: ((anchor?: string) => void) | null): void {
  openPicker = fn;
}

function shouldBounce(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  if (!isMobile) return false;

  // Detect ANY available wallet, not just Phantom. Three checks in
  // order of reliability:
  //   1. Wallet Standard registry — every modern wallet registers here
  //      when injected (Phantom, Backpack, Solflare, Glow, …).
  //   2. window.solana — legacy Phantom-style injection (also Brave,
  //      Math, Slope).
  //   3. Other known globals — wallets that pre-date Wallet Standard.
  let hasStandardWallet = false;
  try {
    hasStandardWallet = getWallets().get().length > 0;
  } catch {
    // @wallet-standard/app may throw on stale/unsupported envs.
  }
  const w = window as any;
  const hasLegacyInjected = typeof w.solana?.connect === 'function';
  const hasOtherInjected = !!(
    w.backpack ||
    w.solflare?.isSolflare ||
    w.glowSolana ||
    w.coin98?.sol ||
    w.trustwallet?.solana ||
    w.bitkeep?.solana
  );
  return !hasStandardWallet && !hasLegacyInjected && !hasOtherInjected;
}

/**
 * Build the deepnets URL we want a wallet to load in its in-app browser.
 * Strips any existing hash so we can pin a fresh anchor (e.g. 'swap').
 */
export function buildBounceTarget(anchor?: string): string {
  const base = `${window.location.origin}${window.location.pathname}${window.location.search}`;
  return anchor ? `${base}#${anchor}` : window.location.href;
}

/**
 * Universal-link builders for each wallet that supports the
 * "open this URL in my in-app browser" pattern. All four (Phantom,
 * Solflare, Backpack) share the same `<host>/ul/[v1/]browse/<url>?ref=<origin>`
 * shape — the only differences are host and the v1 path segment.
 *
 * Jupiter Mobile has no published browse universal-link; users have
 * to open Jupiter manually and paste the URL. The picker UI surfaces
 * a "copy URL" button for that path.
 */
export type WalletBounceId = 'phantom' | 'solflare' | 'backpack';

export function buildWalletBounceUrl(wallet: WalletBounceId, anchor?: string): string {
  const target = encodeURIComponent(buildBounceTarget(anchor));
  const ref = encodeURIComponent(window.location.origin);
  switch (wallet) {
    case 'phantom':
      return `https://phantom.app/ul/browse/${target}?ref=${ref}`;
    case 'solflare':
      return `https://solflare.com/ul/v1/browse/${target}?ref=${ref}`;
    case 'backpack':
      return `https://backpack.app/ul/v1/browse/${target}?ref=${ref}`;
  }
}

/**
 * Public API. Caller convention is unchanged: returns true → bail out
 * of the handler; false → fall through to the normal wallet-adapter modal.
 *
 * Pass `anchor` to scroll to a specific section after the page reloads
 * inside the wallet's webview (e.g. 'swap' to land on the buy/sell panel).
 */
export function redirectToPhantomIfMobile(anchor?: string): boolean {
  if (!shouldBounce()) return false;

  // Picker is the new path — let the user choose Phantom vs Solflare
  // vs Backpack vs "I'll paste it into Jupiter".
  if (openPicker) {
    openPicker(anchor);
    return true;
  }

  // Picker not yet registered (App.tsx render race) — fall back to the
  // original Phantom auto-bounce so a tap during page load isn't lost.
  window.location.href = buildWalletBounceUrl('phantom', anchor);
  return true;
}
